import { useMemo, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, MapPin, Sprout, ChefHat, Layers } from "lucide-react";
import NavBar from "@/components/NavBar";
import SEO from "@/components/SEO";
import { api, formatDate } from "@/lib/api";
import { buildTocFromHtml } from "@/lib/blogToc";

export default function BlogPostPage() {
  const { slug } = useParams();
  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blogPost", slug],
    queryFn: async () => (await api.get(`/blog/${slug}`)).data,
    retry: false,
  });

  // Compute TOC + updated HTML with anchor IDs — ONE pass, memoized on content change
  const { toc, htmlWithIds } = useMemo(() => {
    if (!post?.content_html) return { toc: [], htmlWithIds: "" };
    const res = buildTocFromHtml(post.content_html);
    return { toc: res.toc, htmlWithIds: res.html };
  }, [post?.content_html]);

  // Build JSON-LD (BlogPosting + optional FAQPage + optional Recipe)
  const jsonLd = useMemo(() => {
    if (!post) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/blog/${post.slug}`;
    const graph = [
      {
        "@type": "BlogPosting",
        "@id": `${url}#blogposting`,
        headline: post.meta_title || post.title,
        description: post.meta_description || post.excerpt,
        image: post.cover_image_url
          ? (post.cover_image_url.startsWith("http") ? post.cover_image_url : `${origin}${post.cover_image_url}`)
          : `${origin}/hero-garden.jpg`,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: {
          "@type": "Person",
          name: post.author?.name || "Heather Mich",
          description: post.author?.credentials || "",
          address: {
            "@type": "PostalAddress",
            addressLocality: post.author?.location || "Chino Valley, AZ",
          },
        },
        publisher: {
          "@type": "Organization",
          name: "Graceful Garden Coaching",
          logo: { "@type": "ImageObject", url: `${origin}/brand-logo.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        keywords: [...(post.tags || []), ...(post.categories || [])].join(", "),
        wordCount: post.word_count,
        articleSection: post.categories?.[0] || post.tags?.[0] || "Homestead education",
      },
    ];
    if (post.faq && post.faq.length > 0) {
      graph.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }
    // Recipe schema — added when the post has structured recipe fields
    if (post.recipe) {
      const r = post.recipe;
      const iso = (m) => (m ? `PT${m}M` : undefined);
      const recipeSchema = {
        "@type": "Recipe",
        "@id": `${url}#recipe`,
        name: post.title,
        author: { "@type": "Person", name: post.author?.name || "Heather Mich" },
        description: post.excerpt,
        image: post.cover_image_url || `${origin}/hero-garden.jpg`,
        datePublished: post.published_at,
        recipeYield: r.recipe_yield || undefined,
        recipeCategory: r.course || undefined,
        recipeCuisine: r.cuisine || undefined,
        prepTime: iso(r.prep_time_min),
        cookTime: iso(r.cook_time_min),
        totalTime: iso(r.total_time_min ?? ((r.prep_time_min || 0) + (r.cook_time_min || 0))),
        recipeIngredient: (r.ingredients || []).filter(Boolean),
        recipeInstructions: (r.instructions || []).filter(Boolean).map((step, i) => ({
          "@type": "HowToStep", position: i + 1, text: step,
        })),
      };
      graph.push(recipeSchema);
    }
    return { "@context": "https://schema.org", "@graph": graph };
  }, [post]);

  // Fire view-tracking pixel once per mount per post (rate-limited server-side)
  const viewedRef = useRef(null);
  useEffect(() => {
    if (!post?.slug || viewedRef.current === post.slug) return;
    viewedRef.current = post.slug;
    // Best-effort; ignore errors
    api.post(`/blog/${post.slug}/view`).catch(() => {});
  }, [post?.slug]);

  if (error) {
    return (
      <>
        <SEO title="Post not found" noindex />
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h1 className="font-heading text-3xl">Post not found</h1>
          <Link to="/blog" className="btn-pill btn-outline inline-flex mt-6">Back to journal</Link>
        </main>
      </>
    );
  }

  if (isLoading || !post) {
    return (
      <>
        <SEO title="Loading…" />
        <NavBar />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <div className="h-96 rounded-2xl bg-gg-surface border border-gg-border animate-pulse" />
        </main>
      </>
    );
  }

  const canonical = post.canonical_url ||
    (typeof window !== "undefined" ? `${window.location.origin}/blog/${post.slug}` : "");

  return (
    <>
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        canonical={canonical}
        image={post.cover_image_url || "/hero-garden.jpg"}
        type="article"
        publishedTime={post.published_at}
        modifiedTime={post.updated_at}
        author={post.author?.name}
        jsonLd={jsonLd}
        keywords={(post.tags || []).join(", ")}
      />
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link to="/blog" className="text-sm text-gg-brown hover:text-gg-green inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> All journal entries
        </Link>

        {post.cover_image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 border border-gg-border bg-gg-surface">
            <img
              src={post.cover_image_url}
              alt={post.cover_image_alt || post.title}
              className="w-full h-auto"
            />
          </div>
        )}

        <header className="mb-6">
          <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
            <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.reading_time_minutes} min read</span>
            {post.updated_at && post.updated_at.slice(0, 10) !== (post.published_at || "").slice(0, 10) && (
              <>
                <span aria-hidden="true">·</span>
                <span className="text-gg-muted normal-case tracking-normal">Updated {formatDate(post.updated_at)}</span>
              </>
            )}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-3 leading-tight">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-gg-muted mt-4 leading-relaxed">{post.excerpt}</p>}
        </header>

        <AuthorInline author={post.author} />

        {post.key_takeaways && post.key_takeaways.length > 0 && (
          <KeyTakeaways items={post.key_takeaways} />
        )}

        {toc.length >= 3 && <TableOfContents items={toc} />}

        <article
          className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-[var(--gg-text)] prose-a:text-gg-green prose-strong:text-[var(--gg-text)] prose-img:rounded-xl scroll-mt-24"
          dangerouslySetInnerHTML={{ __html: htmlWithIds }}
        />

        {post.recipe && <RecipeCard recipe={post.recipe} />}

        {post.cta && post.cta.type && post.cta.type !== "none" && (
          <CtaButton slug={post.slug} cta={post.cta} />
        )}

        {post.is_hub && post.spokes && post.spokes.length > 0 && (
          <HubSpokes spokes={post.spokes} />
        )}

        {post.faq && post.faq.length > 0 && <FaqSection items={post.faq} />}

        <RelatedPosts slug={post.slug} />

        {(post.categories?.length > 0 || post.tags?.length > 0) && (
          <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-taxonomy-pills">
            {(post.categories || []).map((c) => (
              <Link
                key={`c-${c}`}
                to={`/blog?category=${encodeURIComponent(c)}`}
                className="text-xs uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-gg-green/15 text-gg-green hover:bg-gg-green/25"
              >
                {c}
              </Link>
            ))}
            {(post.tags || []).map((t) => (
              <Link
                key={`t-${t}`}
                to={`/blog?tag=${encodeURIComponent(t)}`}
                className="text-xs uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}

        <AuthorBlock author={post.author} />

        <div className="mt-12 pt-8 border-t border-gg-border text-center">
          <p className="text-sm text-gg-muted">Want personalized homestead coaching?</p>
          <Link to="/services" className="btn-pill btn-primary inline-flex mt-4">
            See our 1-on-1 services
          </Link>
        </div>
      </main>
    </>
  );
}

/* ─────────── Sub-components ─────────── */

function AuthorInline({ author }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gg-border">
      <div className="w-10 h-10 rounded-full bg-gg-green/15 flex items-center justify-center text-gg-green">
        <Sprout size={20} />
      </div>
      <div className="text-sm">
        <p className="font-semibold text-[var(--gg-text)]">{author.name}</p>
        <p className="text-xs text-gg-muted">{author.credentials}</p>
      </div>
    </div>
  );
}

function KeyTakeaways({ items }) {
  return (
    <aside
      className="rounded-2xl bg-gg-green/5 border border-gg-green/20 p-5 sm:p-6 mb-8"
      aria-label="Key takeaways"
      data-testid="blog-key-takeaways"
    >
      <p className="text-xs uppercase tracking-[0.18em] text-gg-green font-semibold mb-3">Key takeaways</p>
      <ul className="space-y-2 text-sm sm:text-base">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-gg-green mt-1" aria-hidden="true">✦</span>
            <span className="text-[var(--gg-text)]">{it}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function TableOfContents({ items }) {
  return (
    <nav
      className="rounded-2xl bg-gg-surface border border-gg-border p-5 mb-8"
      aria-label="Table of contents"
      data-testid="blog-toc"
    >
      <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">In this post</p>
      <ol className="space-y-1.5 text-sm">
        {items.map((it, idx) => (
          <li key={it.id} className={it.level === 3 ? "pl-4" : ""}>
            <a
              href={`#${it.id}`}
              className="text-gg-brown hover:text-gg-green transition-colors"
            >
              {idx + 1}. {it.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function FaqSection({ items }) {
  return (
    <section
      className="mt-12 pt-8 border-t border-gg-border"
      aria-label="Frequently asked questions"
      data-testid="blog-faq"
    >
      <h2 className="font-heading text-3xl text-[var(--gg-text)] mb-6">Frequently asked</h2>
      <div className="space-y-4">
        {items.map((f, i) => (
          <details
            key={i}
            className="rounded-2xl bg-gg-surface border border-gg-border p-5 group"
            data-testid={`blog-faq-item-${i}`}
          >
            <summary className="cursor-pointer font-semibold text-[var(--gg-text)] flex justify-between items-center">
              <span>{f.question}</span>
              <span className="text-gg-green group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
            </summary>
            <div
              className="mt-3 text-gg-muted leading-relaxed"
              dangerouslySetInnerHTML={{ __html: f.answer }}
            />
          </details>
        ))}
      </div>
    </section>
  );
}

function RelatedPosts({ slug }) {
  const { data: related = [] } = useQuery({
    queryKey: ["blogRelated", slug],
    queryFn: async () => (await api.get(`/blog/${slug}/related?limit=3`)).data,
  });
  if (!related || related.length === 0) return null;
  return (
    <section
      className="mt-12 pt-8 border-t border-gg-border"
      aria-label="Related journal posts"
      data-testid="blog-related-posts"
    >
      <h2 className="font-heading text-2xl text-[var(--gg-text)] mb-5">Keep reading</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {related.map((p) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug}`}
            className="group rounded-2xl border border-gg-border bg-gg-surface overflow-hidden hover:border-gg-green transition-colors"
            data-testid={`blog-related-${p.id}`}
          >
            {p.cover_image_url && (
              <div className="aspect-[16/10] bg-gg-bg overflow-hidden">
                <img
                  src={p.cover_image_url}
                  alt={p.cover_image_alt || p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-gg-brown font-semibold">
                {formatDate(p.published_at)}
              </p>
              <h3 className="font-heading text-base text-[var(--gg-text)] mt-1 line-clamp-2 group-hover:text-gg-green transition-colors">
                {p.title}
              </h3>
              {p.excerpt && (
                <p className="text-xs text-gg-muted mt-2 line-clamp-2">{p.excerpt}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AuthorBlock({ author }) {
  if (!author) return null;
  return (
    <section
      className="mt-10 rounded-2xl bg-gg-bg border border-gg-border p-5 sm:p-6"
      aria-label="About the author"
      data-testid="blog-author-block"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-gg-green/15 flex-shrink-0 flex items-center justify-center text-gg-green">
          <Sprout size={26} />
        </div>
        <div className="flex-1 text-sm">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-1">About the author</p>
          <p className="font-heading text-lg text-[var(--gg-text)]">{author.name}</p>
          <p className="text-gg-muted mt-1">
            {author.credentials}. Based in{" "}
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} className="text-gg-green" />
              {author.location || "Chino Valley, AZ"}
            </span>.
          </p>
        </div>
      </div>
    </section>
  );
}

/* Recipe card — styled block rendered above CTA when post.recipe present. Also feeds Recipe JSON-LD in <head>. */
function RecipeCard({ recipe }) {
  if (!recipe) return null;
  const times = [];
  if (recipe.prep_time_min) times.push(["Prep", `${recipe.prep_time_min} min`]);
  if (recipe.cook_time_min) times.push(["Cook", `${recipe.cook_time_min} min`]);
  if (recipe.total_time_min) times.push(["Total", `${recipe.total_time_min} min`]);
  return (
    <section
      className="not-prose mt-10 rounded-2xl border border-gg-green/30 bg-gg-green/5 p-5 sm:p-6"
      data-testid="blog-recipe-card"
      aria-label="Recipe card"
    >
      <div className="flex items-center gap-2 mb-4">
        <ChefHat className="text-gg-green" size={20} />
        <h2 className="font-heading text-2xl text-[var(--gg-text)] m-0">Recipe</h2>
      </div>
      <div className="grid sm:grid-cols-[1fr_1fr] gap-4 text-sm mb-4">
        {recipe.recipe_yield && (
          <p><strong className="text-gg-brown">Yield:</strong> {recipe.recipe_yield}</p>
        )}
        {recipe.course && (
          <p><strong className="text-gg-brown">Course:</strong> {recipe.course}</p>
        )}
        {recipe.cuisine && (
          <p><strong className="text-gg-brown">Cuisine:</strong> {recipe.cuisine}</p>
        )}
        {times.length > 0 && (
          <p className="flex flex-wrap gap-x-3 gap-y-1">
            {times.map(([label, val]) => (
              <span key={label}><strong className="text-gg-brown">{label}:</strong> {val}</span>
            ))}
          </p>
        )}
      </div>
      {(recipe.ingredients || []).length > 0 && (
        <div className="mb-5">
          <h3 className="font-heading text-lg text-[var(--gg-text)] mb-2">Ingredients</h3>
          <ul className="space-y-1.5 text-sm sm:text-base">
            {recipe.ingredients.filter(Boolean).map((ing, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-gg-green mt-1" aria-hidden="true">·</span>
                <span>{ing}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(recipe.instructions || []).length > 0 && (
        <div className="mb-4">
          <h3 className="font-heading text-lg text-[var(--gg-text)] mb-2">Instructions</h3>
          <ol className="space-y-2 text-sm sm:text-base list-decimal pl-5">
            {recipe.instructions.filter(Boolean).map((step, i) => (
              <li key={i} className="pl-1">{step}</li>
            ))}
          </ol>
        </div>
      )}
      {recipe.notes && (
        <div className="text-sm italic text-gg-muted border-t border-gg-green/20 pt-3">
          <strong className="not-italic text-gg-brown">Notes:</strong> {recipe.notes}
        </div>
      )}
    </section>
  );
}

/* Explicit CTA button. Records a click server-side (rate-limited), then navigates. */
function CtaButton({ slug, cta }) {
  const label = cta.label || {
    class: cta.target_title ? `Register — ${cta.target_title}` : "Register for this class",
    consultation: "Request a Property Consultation",
    "group-class": "Book a Private Group Class",
    waitlist: "Join the Waitlist",
    external: "Learn more",
  }[cta.type] || "Learn more";
  const targetUrl = cta.target_url || cta.url || (
    cta.type === "consultation" ? "/consultation" :
    cta.type === "group-class" ? "/group-class" :
    cta.type === "waitlist" ? "/#waitlist" :
    "/services"
  );
  const isExternal = cta.type === "external" && /^https?:\/\//i.test(targetUrl);
  const handleClick = () => {
    // Fire-and-forget tracking; don't block navigation
    api.post(`/blog/${slug}/cta-click`).catch(() => {});
  };
  return (
    <div className="mt-10 rounded-2xl bg-gg-brown/5 border border-gg-brown/20 p-5 sm:p-6 text-center" data-testid="blog-cta-block">
      {cta.type === "class" && cta.target_class_date && (
        <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
          Next session: {formatDate(cta.target_class_date)}
        </p>
      )}
      {isExternal ? (
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="btn-pill btn-primary inline-flex"
          data-testid="blog-cta-button"
        >
          {label} →
        </a>
      ) : (
        <Link
          to={targetUrl}
          onClick={handleClick}
          className="btn-pill btn-primary inline-flex"
          data-testid="blog-cta-button"
        >
          {label} →
        </Link>
      )}
    </div>
  );
}

/* Hub → grid of spoke posts. Rendered only when post.is_hub and post.spokes present. */
function HubSpokes({ spokes }) {
  return (
    <section
      className="mt-12 pt-8 border-t border-gg-border"
      aria-label="Spoke posts"
      data-testid="blog-hub-spokes"
    >
      <div className="flex items-center gap-2 mb-5">
        <Layers className="text-gg-green" size={20} />
        <h2 className="font-heading text-2xl text-[var(--gg-text)] m-0">Deep-dives from this guide</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {spokes.map((s) => (
          <Link
            key={s.id}
            to={`/blog/${s.slug}`}
            className="group rounded-2xl border border-gg-border bg-gg-surface overflow-hidden hover:border-gg-green transition-colors"
            data-testid={`blog-spoke-${s.id}`}
          >
            {s.cover_image_url && (
              <div className="aspect-[16/10] bg-gg-bg overflow-hidden">
                <img
                  src={s.cover_image_url}
                  alt={s.cover_image_alt || s.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <div className="p-4">
              <h3 className="font-heading text-base text-[var(--gg-text)] group-hover:text-gg-green transition-colors line-clamp-2">
                {s.title}
              </h3>
              {s.excerpt && <p className="text-xs text-gg-muted mt-2 line-clamp-3">{s.excerpt}</p>}
              <p className="text-[10px] uppercase tracking-[0.16em] text-gg-brown mt-2">
                {s.reading_time_minutes} min read
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
